'use client';

import { useState } from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import { IconButton } from '../../../components/icon-button';
import { MonoSmall } from '../../../components/typography';
import { Tooltip } from '../../../components/tooltip';
import { toasterApi } from '../../toast/ToastProvider';
import { Tick01Icon, Copy01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';

export type InternalNetworkCopyableTruncatedMonoValueProps = {
  displayValue: string;
  copyValue: string;
  copyAriaLabel: string;
};

export function InternalNetworkCopyableTruncatedMonoValue({
  displayValue,
  copyValue,
  copyAriaLabel,
}: InternalNetworkCopyableTruncatedMonoValueProps) {
  const tAdmin = useTranslations(
    'designSystem.networkSettings.internalConnection',
  );
  const tCommon = useTranslations('designSystem.common');
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    if (
      !copyValue ||
      typeof navigator === 'undefined' ||
      !navigator.clipboard
    ) {
      return;
    }

    try {
      await navigator.clipboard.writeText(copyValue);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (error: unknown) {
      console.error(
        new Error('Failed to copy value to clipboard', { cause: error }),
      );
      toasterApi.error({
        title: tCommon('error'),
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  return (
    <HStack
      gap={1}
      alignItems="center"
      minW={0}
      w="full"
      maxW={{ base: 'full', md: '480px' }}
    >
      <Box flex={1} minW={0} overflow="hidden">
        <Tooltip title={copyValue} positioning={{ strategy: 'fixed' }}>
          <Box display="block" w="full" minW={0} overflow="hidden">
            <MonoSmall
              fontFamily="mono"
              color="fg.neutral.default"
              truncate
              w="full"
              textAlign="right"
            >
              {displayValue}
            </MonoSmall>
          </Box>
        </Tooltip>
      </Box>
      <Box flexShrink={0}>
        <Tooltip title={copied ? tAdmin('copied') : copyAriaLabel}>
          <IconButton
            variant="ghost"
            size="sm"
            aria-label={copyAriaLabel}
            onClick={() => void handleCopy()}
          >
            {copied ? <Tick01Icon size={16} /> : <Copy01Icon size={16} />}
          </IconButton>
        </Tooltip>
      </Box>
    </HStack>
  );
}
